import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import AIChatBot from '../ai/AIChatBot';
import AdvancedVoiceAssistant from '../ai/AdvancedVoiceAssistant';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCountryList, getDocumentTypePurchaseList, getDocumentTypeSalesList, getIncoTermsList, getQuantityTypeList, getSalesPersonList, getUomTypeList, getCommodityGroup, getContainerType, GetOperator, getMoveType, GetCurrencyList, GetChargeBasisList, getUomList,
    getFileManagerList, getMasterCompanyDetails,
    GetCarrierList,
    GetOfficeTypeList,
    GetNotesTypeList,
    GetPackageTypeList,
    GetBlTypeList,
    GetColoaderList,
} from "../../redux/Common/actions";
import ToastListener from '../ui/ToastListener';

const DashboardLayout = ({ children }) => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { loader } = useSelector(state => state.loader);
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const companyId = authUser?.companyId;

    const topbarRef = useRef(null);
    const sidebarRef = useRef(null);
    useEffect(() => {
        const update = () => {
            const topH = topbarRef.current?.offsetHeight ?? 0;
            const sideW = sidebarRef.current?.offsetWidth ?? 0;
            document.documentElement.style.setProperty('--navbar-height', `${topH}px`);
            document.documentElement.style.setProperty('--sidebar-width', `${sideW}px`);
        };

        update();

        const ro = new ResizeObserver(update);
        if (topbarRef.current) ro.observe(topbarRef.current);
        if (sidebarRef.current) ro.observe(sidebarRef.current);

        return () => ro.disconnect();
    }, []);
    // Close sidebar on route change (mobile)
    useEffect(() => {
        setIsSidebarOpen(false);
    }, [location.pathname]);

    // Close sidebar when clicking outside on mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setIsSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadCommonData = async () => {
            try {
                // await dispatch(getCountryList());
                // await dispatch(getIncoTermsList());
                // await dispatch(getUomTypeList());
                // await dispatch(getSalesPersonList());
                // await dispatch(getDocumentTypePurchaseList());
                // await dispatch(getDocumentTypeSalesList());
                // await dispatch(getQuantityTypeList());
                // await dispatch(getCommodityGroup());
                // await dispatch(getContainerType());
                // await dispatch(GetOperator());
                // await dispatch(getMoveType());
                // await dispatch(GetCurrencyList());
                // await dispatch(GetChargeBasisList());
                // await dispatch(getUomList());
                // await dispatch(getFileManagerList());
                // await dispatch(GetCarrierList());
                await Promise.allSettled([
                    dispatch(getCountryList()),
                    dispatch(getIncoTermsList()),
                    dispatch(getUomTypeList()),
                    dispatch(getSalesPersonList()),
                    dispatch(getDocumentTypePurchaseList()),
                    dispatch(getDocumentTypeSalesList()),
                    dispatch(getQuantityTypeList()),
                    dispatch(getCommodityGroup()),
                    dispatch(getContainerType()),
                    dispatch(GetOperator()),
                    dispatch(getMoveType()),
                    dispatch(GetCurrencyList()),
                    dispatch(GetChargeBasisList()),
                    dispatch(getUomList()),
                    dispatch(getFileManagerList()),
                    dispatch(GetCarrierList()),
                    dispatch(GetOfficeTypeList(companyId)),
                    dispatch(GetNotesTypeList(companyId)),
                    // dispatch(GetProfitCenterList(companyId)),
                    dispatch(GetPackageTypeList()),
                    dispatch(GetBlTypeList(companyId)),
                    dispatch(GetColoaderList(companyId)),
                    ...(authUser?.companyId
                        ? [dispatch(getMasterCompanyDetails(authUser.companyId))]
                        : []),
                ]);
            } catch (err) {
                console.warn("Some common data failed to load:", err);
            }
            setLoading(false);
        };
        loadCommonData();
    }, []);

    // Get current page from URL
    const getCurrentPage = () => {
        const path = location.pathname.replace('/', '');
        return path || 'dashboard';
    };

    // Handle voice navigation
    const handleVoiceNavigation = (page) => {
        console.log('Voice navigation to:', page);
        navigate(`/${page}`);
    };

    // Handle voice action execution
    const handleVoiceActionExecution = async (action) => {
        console.log('Executing action:', action);

        try {
            switch (action.type) {
                case 'compose_email':
                    if (!location.pathname.includes('mailbox')) {
                        navigate('/mailbox', {
                            state: {
                                voiceCommand: {
                                    action: 'compose',
                                    data: {
                                        to: action.params.to || '',
                                        subject: action.params.subject || '',
                                        body: action.params.body || '',
                                        cc: action.params.cc || '',
                                        autoSend: false
                                    }
                                }
                            }
                        });
                    }
                    toast.success('Opening email compose');
                    break;

                case 'send_email':
                    navigate('/mailbox', {
                        state: {
                            voiceCommand: {
                                action: 'compose',
                                data: {
                                    to: action.params.to || '',
                                    subject: action.params.subject || '',
                                    body: action.params.body || '',
                                    cc: action.params.cc || '',
                                    autoSend: true
                                }
                            }
                        }
                    });
                    toast.success('Composing and sending email');
                    break;

                case 'fill_form':
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const field = document.querySelector(`[name="${action.params.field}"]`) ||
                        document.querySelector(`input[placeholder*="${action.params.field}" i]`) ||
                        document.querySelector(`textarea[placeholder*="${action.params.field}" i]`);

                    if (field) {
                        field.value = action.params.value;
                        field.dispatchEvent(new Event('input', { bubbles: true }));
                        field.dispatchEvent(new Event('change', { bubbles: true }));
                        toast.success(`Filled ${action.params.field}`);
                    }
                    break;

                case 'click_button':
                    await new Promise(resolve => setTimeout(resolve, 300));
                    const button = Array.from(document.querySelectorAll('button')).find(
                        btn => btn.textContent.toLowerCase().includes(action.params.text.toLowerCase())
                    );

                    if (button) {
                        button.click();
                        toast.success(`Clicked ${action.params.text}`);
                    }
                    break;

                default:
                    console.log('Action type not yet implemented:', action.type);
            }
        } catch (error) {
            console.error('Error executing action:', error);
            toast.error('Failed to execute action');
        }
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-[#0072FF]"></div>
            </div>
        );
    }

    return (
        <div className="flex h-screen bg-slate-100 dark:bg-slate-950 overflow-hidden">
            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Sidebar - Hidden on mobile, shown on lg+ */}
            <div ref={sidebarRef}>
                <Sidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                />
            </div>
            <ToastListener />

            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                <div ref={topbarRef}>
                    <TopBar
                        onChatToggle={() => setIsChatOpen(!isChatOpen)}
                        onMenuToggle={toggleSidebar}
                        isSidebarOpen={isSidebarOpen}
                    />
                </div>

                <main id='main-content' className="flex-1 overflow-x-hidden">
                    {children}
                </main>
            </div>

            <AIChatBot
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                onNavigate={handleVoiceNavigation}
            />

            <AdvancedVoiceAssistant
                onNavigate={handleVoiceNavigation}
                onExecuteAction={handleVoiceActionExecution}
                currentPage={getCurrentPage()}
                hidden={isChatOpen}
            />

            {
                loader ?
                    <div className="min-h-screen w-screen absolute top-0 left-0 flex items-center justify-center bg-[rgba(0,0,0,0.6)] z-50">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-[#0072FF]"></div>
                    </div>
                    : null
            }
        </div>
    );
};

export default DashboardLayout;
